import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AssessmentForm from "@/components/medication_competency/AssessmentForm";
import {
  PillBottle, Plus, ShieldCheck, AlertTriangle, Clock, CheckCircle2, HelpCircle,
  CalendarClock, Filter, Search, FileText, Eye, History, Printer,
} from "lucide-react";

const STATUS_TONE = {
  competent: "bg-emerald-100 text-emerald-800 border-emerald-300",
  requires_supervision: "bg-amber-100 text-amber-900 border-amber-400",
  not_yet_competent: "bg-red-100 text-red-800 border-red-300",
  in_progress: "bg-sky-100 text-sky-800 border-sky-300",
  not_assessed: "bg-slate-100 text-slate-600 border-slate-300",
};

// Short labels for the dashboard status badge. The full outcome sentences
// (used in the form / PDF / staff card) live in the AssessmentForm and PDF
// modules so we don't crowd the table row.
const STATUS_LABEL = {
  competent: "Competent",
  requires_supervision: "Further Supervision",
  not_yet_competent: "Not Yet Demonstrated",
  in_progress: "In Progress",
  not_assessed: "Not Assessed",
};

function KpiCard({ label, value, Icon, tone, testid }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
  };
  return (
    <div className={`border rounded-md px-3 py-2 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold opacity-80">
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded ${STATUS_TONE[status] || STATUS_TONE.not_assessed}`}
      data-testid={`badge-${status}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function MedicationCompetency() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    site_id: params.get("site_id") || "__all",
    outcome: params.get("outcome") || "__all",
    review_state: params.get("review_state") || "__all",
    assessor: params.get("assessor") || "__all",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // assessment id or null

  const load = async () => {
    setLoading(true);
    try {
      const query = {};
      if (filters.q) query.q = filters.q;
      if (filters.site_id !== "__all") query.site_id = filters.site_id;
      if (filters.outcome !== "__all") query.outcome = filters.outcome;
      if (filters.review_state !== "__all") query.review_state = filters.review_state;
      const [s, r, sitesR, staffR] = await Promise.all([
        api.get("/medication-competency/summary"),
        api.get("/medication-competency/staff-rows", { params: query }),
        api.get("/sites").catch(() => ({ data: [] })),
        api.get("/auth/users").catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setRows(r.data || []);
      setSites(sitesR.data || []);
      setStaffOptions(staffR.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.site_id, filters.outcome, filters.review_state]);

  const applySearch = () => {
    const next = { ...filters };
    setParams(Object.entries(next).reduce((acc, [k, v]) => { if (v && v !== "__all") acc[k] = v; return acc; }, {}));
    load();
  };

  const startNew = (staff_user_id) => { setEditing({ new: true, staff_user_id }); setDialogOpen(true); };
  const openExisting = (assessment_id) => { setEditing({ id: assessment_id }); setDialogOpen(true); };

  const filteredAssessors = useMemo(() => (
    Array.from(new Set(rows.map((r) => r.assessor_name).filter(Boolean)))
  ), [rows]);
  const finalRows = useMemo(() => {
    if (filters.assessor && filters.assessor !== "__all") {
      return rows.filter((r) => r.assessor_name === filters.assessor);
    }
    return rows;
  }, [rows, filters.assessor]);

  const printAssessment = async (id) => {
    try {
      const r = await api.get(`/medication-competency/${id}/print`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50/40" data-testid="page-medication-competency">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
            <PillBottle className="w-3 h-3" /> Supervision &amp; Appraisals · Sub-module
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="medcomp-title">Medication Competency</h1>
          <p className="text-xs text-slate-500 mt-0.5">Structured assessment of staff involved in medication administration. Assessor's professional judgement is the final decision — no automatic score.</p>
        </div>
        {canManage && (
          <Button onClick={() => startNew(null)} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-start-new-assessment">
            <Plus className="w-4 h-4 mr-1.5" /> Start Assessment
          </Button>
        )}
      </div>

      <div className="p-4 lg:p-6 space-y-6">
        {/* KPI cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="medcomp-kpis">
            <KpiCard label="Staff In Scope" value={summary.total_staff} Icon={ShieldCheck} tone="slate" testid="kpi-total" />
            <KpiCard label="Competent" value={summary.competent} Icon={CheckCircle2} tone="emerald" testid="kpi-competent" />
            <KpiCard label="Requires Supervision" value={summary.requires_supervision} Icon={AlertTriangle} tone="amber" testid="kpi-requires" />
            <KpiCard label="Not Yet Competent" value={summary.not_yet_competent} Icon={AlertTriangle} tone="red" testid="kpi-not-yet" />
            <KpiCard label="Review Due Soon" value={summary.review_due_soon} Icon={Clock} tone="sky" testid="kpi-due-soon" />
            <KpiCard label="Overdue Reviews" value={summary.review_overdue} Icon={CalendarClock} tone="red" testid="kpi-overdue" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="medcomp-filters">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1"><Search className="w-3 h-3" /> Search staff</div>
            <div className="flex gap-1">
              <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                     placeholder="Name or email…" className="h-9" data-testid="filter-search" />
              <Button size="sm" variant="outline" className="h-9" onClick={applySearch} data-testid="btn-apply-search">Go</Button>
            </div>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Site / House</div>
            <Select value={filters.site_id} onValueChange={(v) => setFilters({ ...filters, site_id: v })}>
              <SelectTrigger className="h-9" data-testid="filter-site"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Outcome</div>
            <Select value={filters.outcome} onValueChange={(v) => setFilters({ ...filters, outcome: v })}>
              <SelectTrigger className="h-9" data-testid="filter-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Any outcome</SelectItem>
                <SelectItem value="competent">Competent</SelectItem>
                <SelectItem value="requires_supervision">Requires supervision</SelectItem>
                <SelectItem value="not_yet_competent">Not yet competent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Review</div>
            <Select value={filters.review_state} onValueChange={(v) => setFilters({ ...filters, review_state: v })}>
              <SelectTrigger className="h-9" data-testid="filter-review"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Any</SelectItem>
                <SelectItem value="due_soon">Due soon</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="on_track">On track</SelectItem>
                <SelectItem value="no_review_date">No review date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Assessor</div>
            <Select value={filters.assessor} onValueChange={(v) => setFilters({ ...filters, assessor: v })}>
              <SelectTrigger className="h-9" data-testid="filter-assessor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All assessors</SelectItem>
                {filteredAssessors.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="medcomp-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Staff Member</th>
                <th className="text-left">Role</th>
                <th className="text-left">Site</th>
                <th className="text-left">Last Assessment</th>
                <th className="text-left">Assessor</th>
                <th className="text-left">Outcome</th>
                <th className="text-left">Next Review</th>
                <th className="text-left">Status</th>
                <th className="text-right pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center text-slate-400 py-8 italic">Loading…</td></tr>}
              {!loading && finalRows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8 italic">No staff match the current filters.</td></tr>}
              {!loading && finalRows.map((r) => {
                const siteName = sites.find((s) => s.id === r.site_id)?.name || "—";
                return (
                  <tr key={r.staff_user_id} className="border-t border-slate-100" data-testid={`row-${r.staff_user_id}`}>
                    <td className="px-3 py-2 font-medium">
                      <Link to={`/staff/${r.staff_user_id}`} className="text-slate-900 hover:text-cyan-800 hover:underline">
                        {r.staff_name}
                      </Link>
                    </td>
                    <td className="text-slate-600 text-[11px]">{(r.staff_role || "").replace("_", " ")}</td>
                    <td className="text-slate-600 text-[11px]">{siteName}</td>
                    <td className="text-slate-600">{r.last_assessment_date || <span className="text-slate-400">—</span>}</td>
                    <td className="text-slate-600 text-[11px]">{r.assessor_name || <span className="text-slate-400">—</span>}</td>
                    <td>
                      {r.outcome ? <StatusBadge status={r.outcome} /> : <span className="text-slate-400 text-[11px]">—</span>}
                    </td>
                    <td className={r.review_state === "overdue" ? "text-red-700 font-semibold" : r.review_state === "due_soon" ? "text-amber-800" : "text-slate-700"}>
                      {r.review_date || <span className="text-slate-400">—</span>}
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="pr-3 text-right whitespace-nowrap">
                      {r.last_assessment_id && (
                        <button onClick={() => openExisting(r.last_assessment_id)} className="text-cyan-700 hover:text-cyan-900 text-[11px] mr-2 inline-flex items-center gap-1" data-testid={`btn-view-${r.staff_user_id}`}>
                          <Eye className="w-3 h-3" /> View
                        </button>
                      )}
                      {r.in_progress_draft_id && canManage && (
                        <button onClick={() => openExisting(r.in_progress_draft_id)} className="text-sky-700 hover:text-sky-900 text-[11px] mr-2 inline-flex items-center gap-1" data-testid={`btn-continue-${r.staff_user_id}`}>
                          <FileText className="w-3 h-3" /> Continue
                        </button>
                      )}
                      {canManage && !r.in_progress_draft_id && (
                        <button onClick={() => startNew(r.staff_user_id)} className="text-emerald-700 hover:text-emerald-900 text-[11px] mr-2 inline-flex items-center gap-1" data-testid={`btn-new-${r.staff_user_id}`}>
                          <Plus className="w-3 h-3" /> New
                        </button>
                      )}
                      {r.last_assessment_id && (
                        <button onClick={() => printAssessment(r.last_assessment_id)} className="text-slate-600 hover:text-slate-900 text-[11px] inline-flex items-center gap-1" data-testid={`btn-print-${r.staff_user_id}`}>
                          <Printer className="w-3 h-3" /> Print
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-slate-500 flex items-start gap-1" data-testid="medcomp-hint">
          <HelpCircle className="w-3 h-3 mt-0.5 text-slate-400" />
          <span>Assessments are locked once assessor sign-off is complete. Subsequent changes are recorded on the amendment history — historical assessments never disappear.</span>
        </div>
      </div>

      {dialogOpen && (
        <AssessmentForm
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          seed={editing}
          currentUser={user}
          staffOptions={staffOptions}
          canManage={canManage}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
